/**
 * Writer form component for DEX writer workflow.
 * Orchestrates reader selection, content input, metadata, encryption, and upload.
 */

import { subscribe, getState } from '../../state/store.js';
import {
  setWriterField,
  setWriterStatus,
  addRecentReader,
  initWriterState,
  loadRecentReaders,
  clearWriterState
} from '../../state/writer.js';
import { renderReaderInput, initReaderInput } from './reader-input.js';
import { renderContentTabs, initContentTabs } from './content-tabs.js';
import { showToast } from './toast.js';
import { getReaderPublicKey, putPayload } from '../../api/payloads.js';
import { encryptForReader } from '../../api/crypto.js';
import { readFileAsArrayBuffer } from '../../utils/file.js';

/**
 * Render the writer form into a container.
 * @param {HTMLElement} container - Container element
 */
export function renderWriterForm(container) {
  const { writer } = getState();
  const disabled = writer.status !== 'idle';

  // Build status text for button
  const statusTexts = {
    idle: 'Encrypt & Send',
    validating: 'Validating...',
    encrypting: 'Encrypting...',
    uploading: 'Uploading...'
  };
  const buttonText = statusTexts[writer.status] || 'Encrypt & Send';
  const showSpinner = writer.status !== 'idle';

  container.innerHTML = `
    <form id="writer-form" class="space-y-6">
      <h2 class="text-xl font-semibold text-gray-800">Send Encrypted Data</h2>

      <!-- Reader Selection -->
      <section>
        <div id="reader-section"></div>
      </section>

      <!-- Content Input -->
      <section>
        <label class="block text-sm font-medium text-gray-700 mb-2">
          Content <span class="text-red-500">*</span>
        </label>
        <div id="content-tabs"></div>
      </section>

      <!-- Metadata Fields -->
      <section class="space-y-4">
        <div>
          <label for="workflow-id" class="block text-sm font-medium text-gray-700 mb-1">
            Workflow ID <span class="text-gray-400 text-xs">(optional)</span>
          </label>
          <input
            type="text"
            id="workflow-id"
            class="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            placeholder="e.g., workflow-123"
            value="${escapeHtml(writer.workflowId || '')}"
            ${disabled ? 'disabled' : ''}
          />
        </div>

        <div>
          <label for="sample-id" class="block text-sm font-medium text-gray-700 mb-1">
            Sample ID <span class="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="sample-id"
            class="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            placeholder="e.g., sample-456"
            value="${escapeHtml(writer.sampleId || '')}"
            ${disabled ? 'disabled' : ''}
          />
        </div>

        <div>
          <label for="stage-id" class="block text-sm font-medium text-gray-700 mb-1">
            Stage ID <span class="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="stage-id"
            class="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            placeholder="e.g., analysis"
            value="${escapeHtml(writer.stageId || '')}"
            ${disabled ? 'disabled' : ''}
          />
        </div>
      </section>

      <!-- Error Display -->
      ${writer.error ? `
        <div class="bg-red-50 border border-red-200 rounded-md p-3">
          <p class="text-red-700 text-sm">${escapeHtml(writer.error)}</p>
        </div>
      ` : ''}

      <!-- Submit Button -->
      <button
        type="submit"
        class="w-full px-4 py-3 bg-green-600 text-white font-medium rounded-md hover:bg-green-700 focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        ${disabled ? 'disabled' : ''}
      >
        ${showSpinner ? `
          <svg class="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        ` : ''}
        ${buttonText}
      </button>
    </form>
  `;
}

/**
 * Initialize the writer form with event handlers and state subscriptions.
 * @param {HTMLElement} container - Container element
 * @returns {Function} Cleanup function
 */
export function initWriterForm(container) {
  // Initialize writer state
  initWriterState();

  const cleanupFunctions = [];

  // Track subcomponent cleanups
  let readerInputCleanup = null;
  let contentTabsCleanup = null;

  /**
   * Re-render the form and re-wire subcomponents.
   */
  function renderAndWire() {
    // Clean up previous subcomponents
    if (readerInputCleanup) {
      readerInputCleanup();
      readerInputCleanup = null;
    }
    if (contentTabsCleanup) {
      contentTabsCleanup();
      contentTabsCleanup = null;
    }

    // Render main form
    renderWriterForm(container);

    // Wire subcomponents
    const { writer } = getState();

    // Reader input section
    const readerSection = container.querySelector('#reader-section');
    if (readerSection) {
      renderReaderInput(readerSection, {
        readerId: writer.readerId,
        validated: writer.readerValidated,
        loading: writer.status === 'validating',
        error: writer.readerError,
        recentReaders: writer.recentReaders || [],
        disabled: writer.status !== 'idle'
      });

      readerInputCleanup = initReaderInput(
        readerSection,
        {
          onValidate: handleValidateReader,
          onSelect: handleSelectReader,
          onClear: handleClearReader,
          onAutoSelect: handleAutoSelectReader
        },
        {
          recentReaders: writer.recentReaders || [],
          validated: writer.readerValidated,
          loading: writer.status === 'validating',
          readerId: writer.readerId
        }
      );
    }

    // Content tabs section
    const contentTabsSection = container.querySelector('#content-tabs');
    if (contentTabsSection) {
      renderContentTabs(contentTabsSection, {
        activeTab: writer.contentTab || 'file',
        file: writer.file,
        fileName: writer.fileName,
        fileError: writer.fileError,
        textContent: writer.textContent,
        textError: writer.textError,
        disabled: writer.status !== 'idle'
      });

      contentTabsCleanup = initContentTabs(
        contentTabsSection,
        {
          onTabChange: handleTabChange,
          onFileChange: handleFileChange,
          onFileClear: handleFileClear,
          onTextChange: handleTextChange
        },
        { activeTab: writer.contentTab || 'file' }
      );
    }

    // Wire metadata field handlers
    wireMetadataFields();

    // Wire form submission
    wireFormSubmit();
  }

  /**
   * Wire metadata field blur handlers.
   */
  function wireMetadataFields() {
    const workflowInput = container.querySelector('#workflow-id');
    const sampleInput = container.querySelector('#sample-id');
    const stageInput = container.querySelector('#stage-id');

    if (workflowInput) {
      workflowInput.addEventListener('blur', () => {
        setWriterField('workflowId', workflowInput.value.trim());
      });
    }

    if (sampleInput) {
      sampleInput.addEventListener('blur', () => {
        setWriterField('sampleId', sampleInput.value.trim());
      });
    }

    if (stageInput) {
      stageInput.addEventListener('blur', () => {
        setWriterField('stageId', stageInput.value.trim());
      });
    }
  }

  /**
   * Wire form submission handler.
   */
  function wireFormSubmit() {
    const form = container.querySelector('#writer-form');
    if (form) {
      form.addEventListener('submit', handleSubmit);
    }
  }

  // Reader input callbacks
  async function handleValidateReader(readerId) {
    setWriterField('readerId', readerId);
    setWriterField('readerError', null);
    setWriterStatus('validating');

    try {
      const result = await getReaderPublicKey(readerId);
      setWriterField('readerPublicKey', result.public_key);
      setWriterField('readerValidated', true);
      addRecentReader(readerId);
      setWriterStatus('idle');
    } catch (error) {
      setWriterField('readerValidated', false);
      setWriterField('readerPublicKey', null);
      setWriterField('readerError', error.message || 'Reader not found');
      setWriterStatus('idle');
    }
  }

  function handleSelectReader(readerId, publicKey) {
    setWriterField('readerId', readerId);
    setWriterField('readerPublicKey', publicKey);
    setWriterField('readerValidated', true);
    setWriterField('readerError', null);
  }

  function handleClearReader() {
    setWriterField('readerId', '');
    setWriterField('readerPublicKey', null);
    setWriterField('readerValidated', false);
    setWriterField('readerError', null);
  }

  function handleAutoSelectReader(readerId, publicKey) {
    setWriterField('readerId', readerId);
    setWriterField('readerPublicKey', publicKey);
    setWriterField('readerValidated', true);
    setWriterField('readerError', null);
  }

  // Content tab callbacks
  function handleTabChange(tab) {
    setWriterField('contentTab', tab);
  }

  function handleFileChange(file, error) {
    if (error) {
      setWriterField('file', null);
      setWriterField('fileName', '');
      setWriterField('fileError', error);
    } else {
      setWriterField('file', file);
      setWriterField('fileName', file ? file.name : '');
      setWriterField('fileError', null);
    }
  }

  function handleFileClear() {
    setWriterField('file', null);
    setWriterField('fileName', '');
    setWriterField('fileError', null);
  }

  function handleTextChange(text) {
    setWriterField('textContent', text);
    // Clear any previous text error when user types
    if (getState().writer.textError) {
      setWriterField('textError', null);
    }
  }

  /**
   * Handle form submission - validate, encrypt, upload.
   */
  async function handleSubmit(e) {
    e.preventDefault();

    const { writer } = getState();

    // Validation
    const errors = [];

    if (!writer.readerValidated || !writer.readerPublicKey) {
      errors.push('Please select a valid reader');
    }

    const hasFile = writer.contentTab === 'file' && writer.file;

    // Get current values from form inputs (in case they haven't blurred yet)
    const sampleInput = container.querySelector('#sample-id');
    const stageInput = container.querySelector('#stage-id');
    const workflowInput = container.querySelector('#workflow-id');
    const textArea = container.querySelector('#text-content');

    // Check text content from DOM for validation
    const textValue = textArea?.value?.trim() || writer.textContent?.trim() || '';
    const hasText = writer.contentTab === 'text' && textValue;

    if (!hasFile && !hasText) {
      errors.push('Please provide content (file or text)');
    }

    const sampleId = sampleInput?.value.trim() || writer.sampleId;
    const stageId = stageInput?.value.trim() || writer.stageId;
    const workflowId = workflowInput?.value.trim() || writer.workflowId;

    if (!sampleId) {
      errors.push('Sample ID is required');
    }

    if (!stageId) {
      errors.push('Stage ID is required');
    }

    if (errors.length > 0) {
      setWriterStatus('idle', errors.join('. '));
      return;
    }

    // Start encryption
    setWriterStatus('encrypting');

    try {
      // Get content to encrypt
      let content;
      if (hasFile) {
        content = await readFileAsArrayBuffer(writer.file);
      } else {
        // Use textValue already computed from DOM during validation
        content = textValue;
      }

      // Encrypt for reader
      const encryptedPayload = await encryptForReader(content, writer.readerPublicKey);

      // Upload
      setWriterStatus('uploading');

      await putPayload({
        workflowId: workflowId || null,
        sampleId,
        stageId,
        readerId: writer.readerId,
        payload: encryptedPayload
      });

      // Success! Show toast then fully reset form
      showToast('Payload sent successfully!', 'success');
      clearWriterState();
    } catch (error) {
      // Error - preserve form input
      setWriterStatus('idle', error.message || 'Upload failed');
      showToast('Failed to send payload', 'error');
    }
  }

  // Subscribe to writer state changes
  const unsubscribe = subscribe((state, path) => {
    if (path.includes('writer') || path === '*') {
      renderAndWire();
    }
  });
  cleanupFunctions.push(unsubscribe);

  // Initial render
  renderAndWire();

  // Return cleanup function
  return function cleanup() {
    if (readerInputCleanup) {
      readerInputCleanup();
    }
    if (contentTabsCleanup) {
      contentTabsCleanup();
    }
    cleanupFunctions.forEach((fn) => fn());
    clearWriterState();
  };
}

/**
 * Escape HTML special characters to prevent XSS.
 * @param {string} str - String to escape
 * @returns {string} Escaped string
 */
function escapeHtml(str) {
  if (!str) return '';
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}
